// frontend/src/pages/AccountPage.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Twitch, LogOut, RefreshCw, Film, User, ExternalLink, Eye, EyeOff, Link } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { toast } from 'sonner';
import { sanitizeTwitchUsername, sanitizeImageUrl } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const SOCIAL_FIELDS = [
  { key: 'twitch',    label: 'Twitch',     placeholder: 'https://twitch.tv/rawklickz',         icon: '🟣' },
  { key: 'instagram', label: 'Instagram',  placeholder: 'https://instagram.com/yourhandle',    icon: '📸' },
  { key: 'twitter',   label: 'X / Twitter',placeholder: 'https://x.com/yourhandle',            icon: '𝕏'  },
  { key: 'youtube',   label: 'YouTube',    placeholder: 'https://youtube.com/@yourchannel',    icon: '▶️' },
  { key: 'tiktok',    label: 'TikTok',     placeholder: 'https://tiktok.com/@yourhandle',      icon: '🎵' },
  { key: 'discord',   label: 'Discord',    placeholder: 'https://discord.gg/yourserver',       icon: '💬' },
  { key: 'website',   label: 'Website',    placeholder: 'https://yourwebsite.com',             icon: '🌐' },
];

const AccountPage = () => {
  const { isLoggedIn, logout, authToken } = useContext(AuthContext);
  const navigate = useNavigate();

  const [profile,      setProfile]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [isPublic,     setIsPublic]     = useState(true);
  const [savingPrivacy,setSavingPrivacy]= useState(false);
  const [socialLinks,  setSocialLinks]  = useState({});
  const [savingSocial, setSavingSocial] = useState(false);
  const [socialSaved,  setSocialSaved]  = useState(false);

  const font = {
    mono:      '"JetBrains Mono", monospace',
    condensed: '"Barlow Condensed", sans-serif',
    body:      '"Barlow", sans-serif',
  };

  useEffect(() => {
    if (!authToken) { setLoading(false); return; }
    fetch(`${BACKEND_URL}/api/twitch/profile`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then(r => r.json())
      .then(data => {
        setProfile(data);
        setIsPublic(data.is_public !== false);
        setSocialLinks(data.social_links || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authToken]);

  const handleLogout = () => { logout(); toast.success('Logged out successfully.'); };
  const handleSwitchAccount = () => { logout(); window.location.href = `${BACKEND_URL}/api/auth/twitch/login?force_login=true`; };

  const togglePrivacy = async () => {
    setSavingPrivacy(true);
    const newVal = !isPublic;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/settings/privacy`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_public: newVal }),
      });
      if (!res.ok) throw new Error();
      setIsPublic(newVal);
      toast.success(newVal ? 'Profile set to public.' : 'Profile set to BGN members only.');
    } catch {
      toast.error('Could not update privacy setting.');
    } finally {
      setSavingPrivacy(false);
    }
  };

  const saveSocialLinks = async () => {
    setSavingSocial(true);
    setSocialSaved(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/settings/social-links`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(socialLinks),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = Array.isArray(err.detail)
          ? err.detail.map(e => e.msg?.replace('Value error, ', '')).join('; ')
          : (err.detail || 'Could not save social links.');
        throw new Error(msg);
      }
      setSocialSaved(true);
      toast.success('Social links saved!');
    } catch (e) {
      toast.error(e.message || 'Could not save social links.');
    } finally {
      setSavingSocial(false);
    }
  };

  const formatNumber = (n) => {
    if (!n) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
  };

  const S = {
    root:     { background: '#000', minHeight: '100vh', color: '#fff', fontFamily: font.body },
    topBar:   { borderBottom: '1px solid #1a1a1a', padding: '0.75rem 2.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', alignItems: 'center' },
    btnGhost: { background: 'none', border: '1px solid #2a2a2a', color: '#888', padding: '0.4rem 1rem', fontFamily: font.mono, fontSize: '0.65rem', letterSpacing: '0.1em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
    page:     { maxWidth: 1100, margin: '0 auto', padding: '3rem 2rem' },
    section:  { marginBottom: '3rem' },
    label:    { fontFamily: font.mono, fontSize: '0.6rem', color: '#FFD700', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '1rem' },
    input:    { background: '#0d0d0d', border: '1px solid #222', color: '#fff', padding: '0.6rem 0.9rem', fontFamily: font.mono, fontSize: '0.72rem', width: '100%', outline: 'none', boxSizing: 'border-box', letterSpacing: '0.02em' },
    btnWhite: { background: '#FFD700', color: '#000', border: 'none', padding: '0.65rem 1.75rem', fontFamily: font.mono, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' },
  };

  // ── Not logged in ──────────────────────────────────────────────────────────
  if (!loading && (!isLoggedIn && !authToken)) return (
    <div style={{ ...S.root, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@900&family=Barlow:wght@300;400&family=JetBrains+Mono:wght@400;700&display=swap');`}</style>
      <div style={{ maxWidth: 420, width: '100%', padding: '0 1.5rem', textAlign: 'center' }}>
        <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '3.5rem', letterSpacing: '-0.02em', marginBottom: '0.25rem' }}>BGN</div>
        <div style={{ fontFamily: font.mono, fontSize: '0.6rem', color: '#444', letterSpacing: '0.2em', marginBottom: '2.5rem' }}>BLACK GAMERS NETWORK</div>
        <div style={{ background: '#111', border: '1px solid #222', padding: '2.5rem' }}>
          <div style={{ fontFamily: font.mono, fontSize: '0.6rem', color: '#555', letterSpacing: '0.15em', marginBottom: '1.5rem' }}>// JOIN THE NETWORK</div>
          <button onClick={() => { window.location.href = `${BACKEND_URL}/api/auth/twitch/login?force_login=true`; }} style={{ ...S.btnWhite, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: '1rem' }}>
            <Twitch size={16} /> CONNECT WITH TWITCH
          </button>
          <p style={{ fontFamily: font.body, fontWeight: 300, fontSize: '0.8rem', color: '#555', lineHeight: 1.6 }}>
            Connect your Twitch account to join BGN, manage your profile, and get discovered.
          </p>
        </div>
      </div>
    </div>
  );

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ ...S.root, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap'); @keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 36, height: 36, border: '2px solid #222', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
        <p style={{ fontFamily: font.mono, fontSize: '0.7rem', color: '#555', letterSpacing: '0.1em' }}>LOADING...</p>
      </div>
    </div>
  );

  const { user, followers, is_live, videos } = profile || {};
  const memberYear = user?.created_at ? new Date(user.created_at).getFullYear() : '—';

  // ── Dashboard ──────────────────────────────────────────────────────────────
  return (
    <div style={S.root}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;900&family=Barlow:wght@300;400&family=JetBrains+Mono:wght@400;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        .bgn-action-card:hover { border-color:#FFD700 !important; background:#1a1400 !important; }
        .bgn-ghost-btn:hover   { border-color:#666 !important; color:#fff !important; }
        .bgn-social-input:focus { border-color:#555 !important; }
      `}</style>

      {/* Top bar */}
      <div style={S.topBar}>
        <button className="bgn-ghost-btn" style={S.btnGhost} onClick={handleSwitchAccount}><RefreshCw size={11} /> SWITCH ACCOUNT</button>
        <button className="bgn-ghost-btn" style={S.btnGhost} onClick={handleLogout}><LogOut size={11} /> LOGOUT</button>
      </div>

      <div style={S.page}>

        {/* Welcome header */}
        <div style={{ ...S.section }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            {user?.profile_image_url && (
              <img src={sanitizeImageUrl(user.profile_image_url) ?? '/images/default-avatar.png'} alt={user.display_name} style={{ width: 72, height: 72, borderRadius: '50%', border: '2px solid #222', filter: 'grayscale(100%)' }} />
            )}
            <div>
              <div style={{ fontFamily: font.mono, fontSize: '0.6rem', color: '#FFD700', letterSpacing: '0.2em', marginBottom: 4 }}>WELCOME BACK</div>
              <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: 'clamp(2rem, 4vw, 3.5rem)', letterSpacing: '-0.02em', lineHeight: 1 }}>@{user?.display_name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                {is_live
                  ? <span style={{ background: '#ff4444', color: '#fff', fontFamily: font.mono, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em', padding: '2px 8px', borderRadius: 2 }}>● LIVE NOW</span>
                  : <span style={{ background: '#1a1a1a', color: '#555', fontFamily: font.mono, fontSize: '0.6rem', letterSpacing: '0.1em', padding: '2px 8px', borderRadius: 2 }}>OFFLINE</span>
                }
                <span style={{ fontFamily: font.mono, fontSize: '0.6rem', color: '#FFD700', letterSpacing: '0.05em' }}>BGN MEMBER SINCE {memberYear}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={{ ...S.section }}>
          <div style={S.label}>// Stats</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1, border: '1px solid #1a1a1a' }}>
            {[
              ['Followers',   formatNumber(followers)],
              ['Total Views', formatNumber(user?.view_count)],
              ['Broadcaster', user?.broadcaster_type || 'affiliate'],
              ['VODs',        String(videos?.length || 0)],
            ].map(([label, value]) => (
              <div key={label} style={{ background: '#0d0d0d', padding: '1.25rem 1.5rem', borderRight: '1px solid #1a1a1a' }}>
                <div style={{ fontFamily: font.mono, fontSize: '0.6rem', color: '#444', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
                <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '1.8rem', letterSpacing: '-0.02em' }}>{value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick actions */}
        <div style={{ ...S.section }}>
          <div style={S.label}>// Quick Actions</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
            <div className="bgn-action-card" style={{ background: '#0d0d0d', border: '1px solid #222', padding: '1.5rem', cursor: 'pointer', transition: 'all 0.15s' }} onClick={() => { const sl = sanitizeTwitchUsername(user?.login); if (sl) navigate(`/profile/${sl}`); }}>
              <User size={20} color="#FFD700" style={{ marginBottom: '0.75rem' }} />
              <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '1.1rem', marginBottom: 4 }}>VIEW MY PROFILE</div>
              <div style={{ fontFamily: font.body, fontWeight: 300, fontSize: '0.8rem', color: '#555', lineHeight: 1.5 }}>See your public profile at /profile/{user?.login}</div>
            </div>
            <div className="bgn-action-card" style={{ background: '#0d0d0d', border: '1px solid #222', padding: '1.5rem', cursor: 'pointer', transition: 'all 0.15s' }} onClick={() => navigate('/account/clips')}>
              <Film size={20} color="#FFD700" style={{ marginBottom: '0.75rem' }} />
              <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '1.1rem', marginBottom: 4 }}>MANAGE CLIPS</div>
              <div style={{ fontFamily: font.body, fontWeight: 300, fontSize: '0.8rem', color: '#555', lineHeight: 1.5 }}>Choose up to 6 clips to feature on your profile</div>
            </div>
            <div className="bgn-action-card" style={{ background: '#0d0d0d', border: '1px solid #222', padding: '1.5rem', cursor: 'pointer', transition: 'all 0.15s' }} onClick={() => { const sl = sanitizeTwitchUsername(user?.login); if (sl) window.open(`https://twitch.tv/${sl}`, '_blank', 'noopener,noreferrer'); }}>
              <ExternalLink size={20} color="#FFD700" style={{ marginBottom: '0.75rem' }} />
              <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '1.1rem', marginBottom: 4 }}>VIEW ON TWITCH</div>
              <div style={{ fontFamily: font.body, fontWeight: 300, fontSize: '0.8rem', color: '#555', lineHeight: 1.5 }}>Open your Twitch channel in a new tab</div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div style={{ ...S.section }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={S.label}>// Social Links</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {socialSaved && <span style={{ fontFamily: font.mono, fontSize: '0.65rem', color: '#FFD700', letterSpacing: '0.1em' }}>✓ SAVED</span>}
              <button onClick={saveSocialLinks} disabled={savingSocial} style={{ ...S.btnWhite, opacity: savingSocial ? 0.6 : 1, cursor: savingSocial ? 'not-allowed' : 'pointer' }}>
                {savingSocial ? 'SAVING...' : 'SAVE LINKS'}
              </button>
            </div>
          </div>
          <div style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', padding: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {SOCIAL_FIELDS.map(({ key, label, placeholder, icon }) => (
              <div key={key}>
                <div style={{ fontFamily: font.mono, fontSize: '0.6rem', color: '#555', letterSpacing: '0.1em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>{icon}</span> {label.toUpperCase()}
                </div>
                <input
                  className="bgn-social-input"
                  style={{ ...S.input, transition: 'border-color 0.15s' }}
                  type="url"
                  placeholder={placeholder}
                  value={socialLinks[key] || ''}
                  onChange={e => { setSocialLinks(prev => ({ ...prev, [key]: e.target.value })); setSocialSaved(false); }}
                />
              </div>
            ))}
          </div>
          <p style={{ fontFamily: font.body, fontWeight: 300, fontSize: '0.78rem', color: '#444', marginTop: '0.5rem' }}>
            These links appear on your public profile. Enter full URLs (e.g. https://instagram.com/yourhandle).
          </p>
        </div>

        {/* Privacy */}
        <div style={{ ...S.section }}>
          <div style={S.label}>// Privacy</div>
          <div style={{ background: '#0d0d0d', border: '1px solid #222', padding: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                {isPublic ? <Eye size={16} color="#fff" /> : <EyeOff size={16} color="#888" />}
                <span style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '1rem' }}>
                  {isPublic ? 'PUBLIC PROFILE' : 'BGN MEMBERS ONLY'}
                </span>
              </div>
              <p style={{ fontFamily: font.body, fontWeight: 300, fontSize: '0.8rem', color: '#555', margin: 0 }}>
                {isPublic ? `Anyone can view your profile at /profile/${user?.login}` : 'Only logged-in BGN members can view your profile'}
              </p>
            </div>
            <button onClick={togglePrivacy} disabled={savingPrivacy} style={{ background: isPublic ? '#1a1a1a' : '#fff', color: isPublic ? '#888' : '#000', border: `1px solid ${isPublic ? '#333' : '#fff'}`, padding: '0.6rem 1.5rem', fontFamily: font.mono, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', cursor: savingPrivacy ? 'not-allowed' : 'pointer', opacity: savingPrivacy ? 0.6 : 1 }}>
              {savingPrivacy ? 'SAVING...' : isPublic ? 'MAKE PRIVATE' : 'MAKE PUBLIC'}
            </button>
          </div>

          {isPublic && user?.login && (
            <div style={{ marginTop: '0.75rem', background: '#0a0a0a', border: '1px solid #1a1a1a', padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: font.mono, fontSize: '0.7rem', color: '#555' }}>{window.location.origin}/profile/{user.login}</span>
              <button onClick={() => { const sl = sanitizeTwitchUsername(user.login); navigator.clipboard.writeText(`${window.location.origin}/profile/${sl}`); toast.success('Link copied!'); }} style={{ background: 'none', border: '1px solid #2a2a2a', color: '#888', padding: '0.35rem 0.9rem', fontFamily: font.mono, fontSize: '0.6rem', letterSpacing: '0.1em', cursor: 'pointer' }}>
                COPY LINK
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AccountPage;